import { DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AppId, EventProcessingMode, PageSyncState, SharedWorkerService } from '@ecam/eventq-components';
import { filter, firstValueFrom, forkJoin, map, Observable, take, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EventInProcessRow } from '../models/entities/events-in-process.model';
import {
  eventIdFromProcessingRoutePath,
  parseAgentTabRoute,
  processingModeFromRoutePath,
} from '../routing/browser-tab-routes';
import { AgentDataService } from './agent-data.service';
import { AgentEventsService } from './agent-events.service';
import { AuthSessionService } from './auth-session.service';
import { BrowserTabNavigationService } from './browser-tab-navigation.service';
import { BrowserTabService } from './browser-tab.service';
import { EventHandleService } from './event-handle.service';

type PageSyncEvent = PageSyncState['events'][number];

/** Peer frontend sync wait when the sibling is present (Escalation chrome only). */
const PEER_SYNC_WAIT_MS = 2000;

/**
 * Rebuilds this window's event tabs once a session exists.
 * Detection: primary lease → Resolve snapshot when that app is open, otherwise backend claimed events → outbound sync.
 * Escalation: primary lease → peer frontend sync → outbound sync (no EventQ restore).
 */
@Injectable({ providedIn: 'root' })
export class PageStateInitService {
  private readonly auth = inject(AuthSessionService);
  private readonly data = inject(AgentDataService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly tabNavigation = inject(BrowserTabNavigationService);
  private readonly agentEvents = inject(AgentEventsService);
  private readonly router = inject(Router);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly shared = inject(SharedWorkerService, { optional: true });

  private initialized = false;
  private readonly workspaceReadyState = signal(false);
  private readonly workspaceInitFailedState = signal(false);

  /** True after this window successfully restores workspace tabs. */
  readonly workspaceReady = this.workspaceReadyState.asReadonly();

  /** True when the latest workspace restore failed. Cleared when a retry starts. */
  readonly workspaceInitFailed = this.workspaceInitFailedState.asReadonly();

  constructor() {
    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      untracked(() => {
        if (!authenticated) {
          this.initialized = false;
          this.workspaceReadyState.set(false);
          this.workspaceInitFailedState.set(false);
          return;
        }

        void this.initialize();
      });
    });
  }

  /** Loads workspace tabs from backend ownership for this agent. */
  async initialize(): Promise<void> {
    if (this.initialized || !this.auth.isAuthenticated()) return;
    if (this.isSecondaryWindow()) return;

    const agentName = this.currentAgentName();
    if (!agentName && this.auth.userId() == null) return;

    this.initialized = true;
    this.workspaceInitFailedState.set(false);
    this.tabNavigation.beginWorkspaceBoot();

    try {
      // Escalation EventQ: mirror Resolve chrome; do not rebuild from in-process.
      if (!this.auth.shouldManageEvents()) {
        await this.whenAgentTabRouteReady();
        this.agentEvents.broadcastStateQuery({ openResolve: await this.shouldOpenPeerResolve() });
        await this.tryApplyPeerPageState();
        this.agentEvents.broadcastStateSync();
        this.workspaceReadyState.set(true);
        return;
      }

      await this.whenAgentTabRouteReady();

      // Same contract as Sync with Resolve: when the sibling is already open, its
      // snapshot is the tab set (every open event), not the single deep-link id.
      const peerOpen = await this.isPeerResolveOpen();
      if (peerOpen) {
        this.agentEvents.broadcastStateQuery({ openResolve: false });
        const applied = await this.tryApplyPeerPageState();
        if (applied) {
          this.agentEvents.broadcastStateSync();
          this.workspaceReadyState.set(true);
          return;
        }
      }

      const events = await firstValueFrom(this.agentOpenWork());
      if (agentName && agentName !== this.currentAgentName()) return;
      await this.tabNavigation.syncFromPageState(this.withRouteOpenEvent({ activeEventId: null, events }), {
        hardSync: true,
        applyEmptyDuringBoot: true,
      });
      this.agentEvents.broadcastStateSync();
      this.workspaceReadyState.set(true);
    } catch {
      this.initialized = false;
      this.workspaceReadyState.set(false);
      this.workspaceInitFailedState.set(true);
    } finally {
      this.tabNavigation.completeWorkspaceBoot();
    }
  }

  private isSecondaryWindow(): boolean {
    if (!environment.features.sharedWorker || !this.shared) return false;
    // Fail closed only after the worker reports presence; before connect, boot normally.
    return this.shared.connected() && this.shared.isPrimary() === false;
  }

  private async isPeerResolveOpen(): Promise<boolean> {
    if (!environment.features.sharedWorker || !this.shared?.connected()) return false;
    return this.shared.isAppOpen(AppId.RESOLVE).catch(() => false);
  }

  /** Fail closed when the worker is disconnected or presence is unknown. */
  private async shouldOpenPeerResolve(): Promise<boolean> {
    if (!environment.features.sharedWorker || !this.shared?.connected()) return false;
    const peerOpen = await this.shared.isAppOpen(AppId.RESOLVE).catch(() => false);
    return !peerOpen;
  }

  private async tryApplyPeerPageState(): Promise<boolean> {
    try {
      const event = await firstValueFrom(
        this.agentEvents.events$.pipe(
          filter((e) => e.type === 'STATE_SYNC' && !!e.pageState),
          take(1),
          timeout({ first: PEER_SYNC_WAIT_MS }),
          takeUntilDestroyed(this.destroyRef),
        ),
      );
      if (!event.pageState) return false;
      const pageState = this.withRouteOpenEvent(event.pageState);
      if (pageState.events.length === 0) return false;
      await this.tabNavigation.syncFromPageState(pageState, { hardSync: true });
      return true;
    } catch {
      return false;
    }
  }

  private agentOpenWork(): Observable<PageSyncEvent[]> {
    return forkJoin({
      openWork: this.data.getAgentOpenWork(),
      handles: this.data.getEventHandles(),
    }).pipe(
      map(({ openWork, handles }) => {
        const owned = this.filterOwned(
          this.eventHandles.mergeInProcessRows(openWork.items, handles.items),
        );
        return owned
          .slice(0, environment.features.maxOpenWorkTabs)
          .map((row) => this.toEvent(row));
      }),
    );
  }

  private toEvent(row: EventInProcessRow): PageSyncEvent {
    if (row.type === 'site-check') return this.toSiteCheckEvent(row);
    else if (row.type === 'manual-patrol') return this.toPatrolEvent(row);
    else return this.toProcessEvent(row);
  }

  private toProcessEvent(row: EventInProcessRow): PageSyncEvent {
    return this.toOpenWorkEvent(row, 'process-event');
  }

  private toSiteCheckEvent(row: EventInProcessRow): PageSyncEvent {
    return this.toOpenWorkEvent(row, 'site-check');
  }

  private toPatrolEvent(row: EventInProcessRow): PageSyncEvent {
    return this.toOpenWorkEvent(row, 'manual-patrol');
  }

  private toOpenWorkEvent(
    row: EventInProcessRow,
    eventProcessingMode: EventProcessingMode,
  ): PageSyncEvent {
    const claimedAt = row.claimedAt?.trim();
    return {
      id: row.id,
      label: row.location,
      eventProcessingMode,
      siteId: this.positiveSiteId(row.siteId),
      processingTimer: claimedAt
        ? {
            correlationId: '',
            entityId: row.id,
            entityType: eventProcessingMode,
            prevElapsedMs: 0,
            segmentStartAt: claimedAt,
            running: true,
          }
        : undefined,
    };
  }

  private positiveSiteId(siteId: number | undefined): number | undefined {
    return Number.isInteger(siteId) && (siteId ?? 0) > 0 ? siteId : undefined;
  }

  /**
   * Prefer numeric dealer user id. When either side lacks an id, fall back to
   * case-insensitive display name.
   */
  private filterOwned(rows: EventInProcessRow[]): EventInProcessRow[] {
    const sessionUserId = this.auth.userId();
    const agentKey = this.currentAgentName()?.toLowerCase() ?? '';
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (seen.has(row.id)) return false;

      const owned =
        sessionUserId != null && row.userId != null
          ? row.userId === sessionUserId
          : !!agentKey && row.agent?.trim().toLowerCase() === agentKey;

      if (!owned) return false;
      seen.add(row.id);
      return true;
    });
  }

  private currentAgentName(): string | null {
    const displayName = this.auth.displayName()?.trim();
    if (displayName) return displayName;
    const emailLocalPart = this.auth.email()?.trim().split('@')[0];
    return emailLocalPart || null;
  }

  /**
   * A refresh keeps the event URL before in-progress ownership or the Resolve
   * snapshot includes that id. Put it on the snapshot that we broadcast back.
   */
  private withRouteOpenEvent(pageState: PageSyncState): PageSyncState {
    if (!this.auth.shouldManageEvents()) {
      return pageState;
    }

    const parsed = parseAgentTabRoute(this.router.url);
    if (!parsed) {
      return pageState;
    }

    const eventId = eventIdFromProcessingRoutePath(parsed.routePath);
    if (!eventId) {
      return pageState;
    }

    const alreadyListed = pageState.events.some((event) => event.id === eventId);
    const existing = this.browserTabs
      .tabDetails()
      .find((tab) => tab.pageState.activeEventId === eventId);
    const label = existing && existing.tab.label !== 'New Event' ? existing.tab.label : eventId;
    return {
      ...pageState,
      activeEventId: eventId,
      events: alreadyListed
        ? pageState.events
        : [
            ...pageState.events,
            {
              id: eventId,
              label,
              eventProcessingMode: processingModeFromRoutePath(parsed.routePath) ?? 'process-event',
            },
          ],
    };
  }

  private async whenAgentTabRouteReady(): Promise<void> {
    if (parseAgentTabRoute(this.router.url)) return;
    await firstValueFrom(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => parseAgentTabRoute(event.urlAfterRedirects) !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      ),
      { defaultValue: null },
    );
  }
}
